import { useState } from "react";
import { pinProblem, PIN_MAX } from "@baros/core";
import { signOut } from "./auth.js";
import { setTabletKey, setTabletSession } from "./tablet.js";
import { tabletsApi } from "./tablets-api.js";

interface Props {
  readonly token: string;
  readonly orgId: string;
  readonly venueId: string | null;
  readonly venueName: string;
  readonly pinSet: boolean;
  /** May enrol a tablet for this venue (`device:enrol`). */
  readonly canEnrol: boolean;
  readonly onPinChanged: () => void;
}

/**
 * The account panel's tablet controls (#80), shown on a proper sign-in only:
 * your own PIN, and setting this device up as the venue's tablet.
 */
export function AccountTablet(p: Props) {
  const [pinOpen, setPinOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [again, setAgain] = useState("");
  const [enrolOpen, setEnrolOpen] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const problem = pin ? pinProblem(pin) : null;
  const mismatch = again.length > 0 && again !== pin;

  return (
    <div className="account-tablet">
      <div className="label">Tablet PIN</div>
      {!pinOpen ? (
        <div className="row" style={{ gap: 8 }}>
          <span style={{ fontSize: 13 }}>{p.pinSet ? "Set" : "Not set"}</span>
          <span className="spacer" />
          <button className="btn sm" onClick={() => { setPinOpen(true); setMsg(null); }}>{p.pinSet ? "Change" : "Set a PIN"}</button>
        </div>
      ) : (
        <form
          className="account-pin"
          onSubmit={async (e) => {
            e.preventDefault();
            if (problem || mismatch || !pin) return;
            setBusy(true);
            try {
              await tabletsApi.setPin(p.token, pin);
              setMsg({ ok: true, text: "PIN saved. Use it to unlock this venue's tablets." });
              setPinOpen(false);
              p.onPinChanged();
            } catch (err) {
              setMsg({ ok: false, text: err instanceof Error ? err.message : "Could not save the PIN" });
            } finally {
              setBusy(false);
              setPin("");
              setAgain("");
            }
          }}
        >
          <input
            type="password"
            inputMode="numeric"
            autoComplete="new-password"
            maxLength={PIN_MAX}
            placeholder="4–6 digits"
            aria-label="New PIN"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
          />
          <input
            type="password"
            inputMode="numeric"
            autoComplete="new-password"
            maxLength={PIN_MAX}
            placeholder="Again"
            aria-label="New PIN again"
            value={again}
            onChange={(e) => setAgain(e.target.value.replace(/\D/g, ""))}
          />
          {(problem || mismatch) && <div className="label warn">{problem ?? "The two do not match"}</div>}
          <div className="row" style={{ gap: 6 }}>
            <button className="btn sm" type="button" onClick={() => setPinOpen(false)}>Cancel</button>
            <button className="btn sm" type="submit" disabled={busy || !pin || Boolean(problem) || mismatch || again !== pin}>
              Save PIN
            </button>
          </div>
        </form>
      )}

      {p.canEnrol && p.venueId && (
        <>
          <div className="label" style={{ marginTop: 10 }}>This device</div>
          {!enrolOpen ? (
            <button className="btn sm" onClick={() => { setEnrolOpen(true); setMsg(null); }}>
              Set up as {p.venueName}'s tablet
            </button>
          ) : (
            <form
              className="account-pin"
              onSubmit={async (e) => {
                e.preventDefault();
                setBusy(true);
                try {
                  const res = await tabletsApi.enrol(p.token, p.venueId!, name);
                  setTabletKey({ deviceId: res.device.deviceId, token: res.token, orgId: p.orgId, venueId: p.venueId!, name: res.device.name });
                  setTabletSession(null);
                  // Signed out, so the tablet comes up locked rather than in
                  // the manager's name.
                  signOut();
                  window.location.assign("/");
                } catch (err) {
                  setMsg({ ok: false, text: err instanceof Error ? err.message : "Could not set up the tablet" });
                  setBusy(false);
                }
              }}
            >
              <div className="muted" style={{ fontSize: 12, lineHeight: 1.45 }}>
                From then on this device opens to a PIN screen for {p.venueName}, and you are signed out of it.
              </div>
              <input type="text" placeholder="Back bar iPad" aria-label="Tablet name" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} required />
              <div className="row" style={{ gap: 6 }}>
                <button className="btn sm" type="button" onClick={() => setEnrolOpen(false)}>Cancel</button>
                <button className="btn sm" type="submit" disabled={busy || !name.trim()}>Set up tablet</button>
              </div>
            </form>
          )}
        </>
      )}
      {msg && <div className={`msg ${msg.ok ? "ok" : "err"}`} style={{ fontSize: 12, padding: "8px 10px" }}>{msg.text}</div>}
    </div>
  );
}
